import { useCallback, useMemo, useState } from 'react'
import type { LifeSpace, Project } from '@/src/renderer/lib/db/models'
import {
  activeTrigger, applySuggestion, rankByQuery, type ParsedTitle, type SuggestKind, type SuggestTrigger, type TitleCatalog,
} from '@/src/renderer/lib/titleParser'
import type { TitleRecognitionPlace } from '../TitleRecognitionScope'

export type SuggestionItem =
  | { type: 'project'; id: string; name: string; icon: string | null; iconColor: string | null; parentName: string | null }
  | { type: 'section'; id: string; name: string }
  | { type: 'tag'; id: string; name: string }
  /** „„büro“ neu anlegen“ — der Tag entsteht wie in B erst beim Speichern. */
  | { type: 'new-tag'; name: string }
  /** „Neuen Abschnitt „Todo“ anlegen“ — entsteht sofort beim Wählen, am Ende des Projekts (Sven, 03.10.2026). */
  | { type: 'new-section'; name: string; projectId: string }

export interface SuggestionGroup {
  key: string
  /** Kopf über der Gruppe: der Bereich (`#`) oder das Projekt der Abschnitte (`/`). */
  lifeSpace?: LifeSpace
  projectName?: string
  items: SuggestionItem[]
}

export interface TitleSuggestions {
  open: boolean
  trigger: SuggestTrigger | null
  groups: SuggestionGroup[]
  /** Alle Einträge in der Reihenfolge der Liste — die Auswahl zählt darin. */
  items: SuggestionItem[]
  highlighted: number
  /** Die Vorauswahl — bei gleichem Namen der Eintrag, den die Erkennung ohnehin nähme. */
  defaultHighlighted: number
  setHighlighted: (index: number) => void
  move: (delta: 1 | -1) => void
  /** Ersetzt das Getippte; neuer Text und Caret, oder `null`, wenn es nichts zu übernehmen gibt. */
  accept: (index?: number) => { text: string; caret: number } | null
  /** Esc: zu, für dieses Zeichen. */
  close: () => void
}

interface UseTitleSuggestionsArgs {
  enabled: boolean
  title: string
  /** Der Caret im Titel, `null` ohne Fokus oder mit Auswahl. */
  caret: number | null
  place: TitleRecognitionPlace | null
  catalog: TitleCatalog
  parsed: ParsedTitle
}

const SPACE_ORDER: LifeSpace[] = ['personal', 'work', 'home']
/** Was als neuer Tag oder Abschnitt taugt: ein Wort — ein Leerzeichen beendet die Suche ohnehin. */
const NEW_NAME = /^[\p{L}\p{N}_-]+$/u

const isActiveProject = (p: Project) => !p.deletedAt && !p.isSystem && (p.status === undefined || p.status === 'active')

/**
 * **Vorschläge nach `#`, `/` und `@`** (#234, Schritt C). Aus Titel und Caret: welches Zeichen sucht,
 * welche Einträge passen, welcher gewählt ist — und was das Übernehmen in den Titel schreibt.
 *
 * `/` nur, wo ein Abschnitt gemeint sein kann: nach `#Projekt` oder auf einer Projektseite.
 */
export function useTitleSuggestions({ enabled, title, caret, place, catalog, parsed }: UseTitleSuggestionsArgs): TitleSuggestions {
  // In der Reihenfolge der Sidebar — wie bei gleichnamigen Projekten in der Erkennung.
  const projects = useMemo(
    () => (enabled && place ? place.projects.filter(isActiveProject).sort((a, b) => a.order - b.order) : []),
    [enabled, place],
  )

  // Der Abschnitt gehört zum `#Projekt` davor, sonst zur Seite.
  const sectionProjectAt = useCallback((start: number) => {
    const before = parsed.tokens.filter(t => t.kind === 'project' && t.end <= start).at(-1)
    if (before) return parsed.fields.project?.id ?? null
    return place?.projectId ?? null
  }, [parsed, place])

  const trigger = useMemo(() => {
    if (!enabled || caret == null) return null
    const found = activeTrigger(title, caret, (kind: SuggestKind) => {
      if (kind === 'project') return projects.map(p => p.name)
      if (kind === 'tag') return catalog.tags.map(t => t.name)
      return []
    })
    if (found?.kind === 'section') {
      const projectId = sectionProjectAt(found.start)
      if (!projectId) return null
      const titles = catalog.sections.filter(s => s.projectId === projectId).map(s => s.title)
      if (/\s/u.test(found.query) && !titles.some(t => t.toLowerCase().startsWith(found.query.toLowerCase()))) return null
    }
    return found
  }, [enabled, caret, title, projects, catalog, sectionProjectAt])

  const groups = useMemo((): SuggestionGroup[] => {
    if (!trigger) return []
    const { query } = trigger
    if (trigger.kind === 'project') {
      const nameOf = new Map(projects.map(p => [p.id, p.name]))
      const ranked = rankByQuery(projects, query, p => p.name)
      const current = place?.lifeSpace
      const spaces = current ? [current, ...SPACE_ORDER.filter(s => s !== current)] : SPACE_ORDER
      return spaces.map(space => ({
        key: space,
        lifeSpace: space,
        items: ranked.filter(p => p.lifeSpace === space).map((p): SuggestionItem => ({
          type: 'project', id: p.id, name: p.name, icon: p.icon ?? null, iconColor: p.iconColor ?? null,
          parentName: p.parentId ? nameOf.get(p.parentId) ?? null : null,
        })),
      })).filter(group => group.items.length > 0)
    }
    if (trigger.kind === 'section') {
      const projectId = sectionProjectAt(trigger.start)
      const sections = catalog.sections.filter(s => s.projectId === projectId)
      const items = rankByQuery(sections, query, s => s.title).map((s): SuggestionItem => ({ type: 'section', id: s.id, name: s.title }))
      const exists = sections.some(s => s.title.toLowerCase() === query.toLowerCase())
      if (projectId && query && !exists && NEW_NAME.test(query)) items.push({ type: 'new-section', name: query, projectId })
      const projectName = catalog.projects.find(p => p.id === projectId)?.name
      return [{ key: 'sections', projectName, items }]
    }
    const items: SuggestionItem[] = rankByQuery(catalog.tags, query, t => t.name).map(t => ({ type: 'tag', id: t.id, name: t.name }))
    const exists = catalog.tags.some(t => t.name.toLowerCase() === query.toLowerCase())
    if (query && !exists && NEW_NAME.test(query)) items.push({ type: 'new-tag', name: query })
    return items.length ? [{ key: 'tags', items }] : []
  }, [trigger, projects, place, catalog, sectionProjectAt])

  const items = useMemo(() => groups.flatMap(group => group.items), [groups])

  // Esc schließt für genau dieses Zeichen; ein neues `#` öffnet wieder.
  const [closedAt, setClosedAt] = useState<number | null>(null)
  if (closedAt !== null && trigger?.start !== closedAt) setClosedAt(null)

  /*
    Die Auswahl beginnt bei jeder neuen Suche oben — außer der getippte Name steht so schon da: Dann steht
    sie auf ihm, bei gleichen Namen auf dem der Seite bzw. dem im Hauptfenster offenen, also dort, wo auch
    die Erkennung landet. Unverändert übernommen ändert Enter so nichts; erst eine andere Zeile überstimmt
    die Regel. Sonst machte Enter aus „#Haus“ das obere „#Hausbau“ (Code-Review 03.10.2026).
  */
  const searchKey = trigger ? `${trigger.kind}:${trigger.start}:${trigger.query}` : ''
  const preferredId = trigger?.kind === 'project' ? place?.projectId ?? place?.preferredProjectId ?? null : null
  const exact = (item: SuggestionItem) => item.type !== 'new-tag' && item.type !== 'new-section'
    && item.name.toLowerCase() === trigger?.query.toLowerCase()
  const preferredExact = items.findIndex(item => exact(item) && item.type === 'project' && item.id === preferredId)
  const defaultHighlighted = Math.max(0, preferredExact >= 0 ? preferredExact : items.findIndex(exact))
  const [highlight, setHighlight] = useState({ key: searchKey, index: 0 })
  const highlighted = highlight.key === searchKey ? Math.min(highlight.index, Math.max(items.length - 1, 0)) : defaultHighlighted
  const setHighlighted = useCallback((index: number) => setHighlight({ key: searchKey, index }), [searchKey])
  const move = useCallback((delta: 1 | -1) => {
    if (items.length === 0) return
    setHighlight({ key: searchKey, index: (highlighted + delta + items.length) % items.length })
  }, [items.length, highlighted, searchKey])

  // Ohne Einträge zeigt nur eine Projektsuche mit Text etwas („Kein Projekt …“); `/` und `@` bieten dann
  // „neu anlegen“ an, leer bleiben sie zu.
  const open = trigger != null && closedAt !== trigger.start
    && (items.length > 0 || (trigger.kind === 'project' && trigger.query !== ''))

  const accept = useCallback((index?: number) => {
    const item = items[index ?? highlighted]
    if (!trigger || !item) return null
    return applySuggestion(title, trigger, item.name)
  }, [items, highlighted, trigger, title])

  const close = useCallback(() => { if (trigger) setClosedAt(trigger.start) }, [trigger])

  return { open, trigger, groups, items, highlighted, defaultHighlighted, setHighlighted, move, accept, close }
}
